import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { supabase } from "@/integrations/supabase/client";
import type { Database } from "@/integrations/supabase/types";
import { toast } from "sonner";
import { Heart, Users, Handshake } from "lucide-react";
import { signUpSchema } from "@/lib/validations/auth";

export default function CommunityOrgSignup() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [consents, setConsents] = useState({
    terms: false,
    privacy: false,
  });
  
  const [formData, setFormData] = useState({
    email: "",
    password: "",
    full_name: "",
    organisation_name: "",
  });

  const handleSignUp = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!consents.terms || !consents.privacy) {
      toast.error("Please accept all required consents to continue");
      return;
    }

    setLoading(true);

    try {
      const validatedData = signUpSchema.parse({
        email: formData.email,
        password: formData.password,
        full_name: formData.full_name,
        role: "employer_service_provider"
      });

      const redirectUrl = `${window.location.origin}/`;
      
      const { data, error } = await supabase.auth.signUp({
        email: validatedData.email,
        password: validatedData.password,
        options: {
          emailRedirectTo: redirectUrl,
          data: {
            full_name: validatedData.full_name,
            organisation_name: formData.organisation_name,
          },
        },
      });

      if (error) throw error;

      if (data.user) {
        const consentRecords: Database["public"]["Tables"]["consent_records"]["Insert"][] = [
          {
            user_id: data.user.id,
            purpose: "essential",
            consent_given: true,
            consent_method: "signup_checkbox",
            consent_text: "I accept the Terms of Service",
            consent_version: "1.0.0",
            ip_address: null,
            user_agent: navigator.userAgent,
          },
          {
            user_id: data.user.id,
            purpose: "essential",
            consent_given: true,
            consent_method: "signup_checkbox",
            consent_text: "I have read and understood the Privacy Policy",
            consent_version: "1.0.0",
            ip_address: null,
            user_agent: navigator.userAgent,
          },
        ];

        await supabase.from("consent_records").insert(consentRecords);

        await supabase.from("user_roles").insert({
          user_id: data.user.id,
          role: "employer_service_provider",
          approval_status: "approved",
        });

        toast.success("Welcome to RePath! Your organisation account is ready.");
        navigate("/onboarding");
      }
    } catch (error: any) {
      if (error.errors) {
        error.errors.forEach((err: any) => {
          toast.error(err.message);
        });
      } else {
        toast.error(error.message || "Sign up failed");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[hsl(var(--community-beige))] flex flex-col relative overflow-hidden">
      {/* Background Illustration */}
      <div className="absolute inset-0 opacity-10 pointer-events-none">
        <div className="absolute top-20 left-10">
          <Users className="w-32 h-32 text-[hsl(var(--community-green))]" />
        </div>
        <div className="absolute bottom-20 right-10">
          <Handshake className="w-40 h-40 text-[hsl(var(--community-coral))]" />
        </div>
        <div className="absolute top-1/2 right-1/4 -translate-y-1/2">
          <Heart className="w-24 h-24 text-[hsl(var(--community-green))]" />
        </div>
      </div>

      {/* Header */}
      <header className="p-6 relative z-10">
        <div className="flex items-center gap-2">
          <Heart className="w-8 h-8 text-[hsl(var(--community-green))]" />
          <h1 className="text-2xl font-bold text-[hsl(var(--community-green))]">RePath</h1>
        </div>
      </header>

      {/* Main Content */}
      <div className="flex-1 flex items-center justify-center p-4 relative z-10">
        <div className="w-full max-w-md">
          <div className="text-center mb-8">
            <h2 className="text-3xl font-bold text-[hsl(var(--community-green))] mb-2">
              Join Our Network
            </h2>
            <p className="text-foreground/80">
              Partner with us to build pathways to success, together.
            </p>
          </div>

          {/* Signup Form */}
          <div className="bg-white rounded-lg shadow-lg p-8 border border-[hsl(var(--community-green))]/20">
            <form onSubmit={handleSignUp} className="space-y-5">
              <div className="space-y-2">
                <Label htmlFor="organisation_name">Organisation Name</Label>
                <Input
                  id="organisation_name"
                  type="text"
                  value={formData.organisation_name}
                  onChange={(e) =>
                    setFormData({ ...formData, organisation_name: e.target.value })
                  }
                  placeholder="Community Support Services"
                  className="border-border"
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="full_name">Your Full Name</Label>
                <Input
                  id="full_name"
                  type="text"
                  value={formData.full_name}
                  onChange={(e) =>
                    setFormData({ ...formData, full_name: e.target.value })
                  }
                  placeholder="Jane Smith"
                  className="border-border"
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="email">Organisation Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) =>
                    setFormData({ ...formData, email: e.target.value })
                  }
                  placeholder="your@organisation.org"
                  className="border-border"
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="password">Password</Label>
                <Input
                  id="password"
                  type="password"
                  value={formData.password}
                  onChange={(e) =>
                    setFormData({ ...formData, password: e.target.value })
                  }
                  placeholder="Create a strong password"
                  className="border-border"
                  required
                />
                <p className="text-xs text-muted-foreground">
                  Min 8 characters, include uppercase, lowercase, and number
                </p>
              </div>

              {/* Consents */}
              <div className="space-y-3 pt-2">
                <div className="flex items-start space-x-2">
                  <Checkbox
                    id="terms"
                    checked={consents.terms}
                    onCheckedChange={(checked) =>
                      setConsents({ ...consents, terms: checked as boolean })
                    }
                    required
                  />
                  <Label htmlFor="terms" className="text-sm cursor-pointer leading-relaxed">
                    I agree to the{" "}
                    <a href="/terms" className="text-[hsl(var(--community-coral))] hover:underline">
                      Terms of Service
                    </a>
                  </Label>
                </div>

                <div className="flex items-start space-x-2">
                  <Checkbox
                    id="privacy"
                    checked={consents.privacy}
                    onCheckedChange={(checked) =>
                      setConsents({ ...consents, privacy: checked as boolean })
                    }
                    required
                  />
                  <Label htmlFor="privacy" className="text-sm cursor-pointer leading-relaxed">
                    I agree to the{" "}
                    <a href="/privacy" className="text-[hsl(var(--community-coral))] hover:underline">
                      Privacy Policy
                    </a>
                  </Label>
                </div>
              </div>

              <Button
                type="submit"
                disabled={loading}
                className="w-full bg-[hsl(var(--community-green))] text-white hover:bg-[hsl(var(--community-green))]/90 font-semibold py-6 text-lg"
              >
                {loading ? "Creating account..." : "Join the Network"}
              </Button>
            </form>

            <div className="mt-6 text-center">
              <p className="text-sm text-muted-foreground">
                Already a partner?{" "}
                <a href="/community-org-auth" className="text-[hsl(var(--community-green))] hover:underline font-semibold">
                  Log In
                </a>
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="p-6 border-t border-border/50 relative z-10">
        <div className="flex flex-wrap justify-center gap-6 text-sm text-muted-foreground">
          <a href="/privacy" className="hover:text-foreground transition-colors">
            Privacy Policy
          </a>
          <a href="/terms" className="hover:text-foreground transition-colors">
            Terms of Service
          </a>
          <a href="mailto:admin@sccapp.co.uk" className="hover:text-foreground transition-colors">
            Contact Support
          </a>
        </div>
      </footer>
    </div>
  );
}
